import IssuesTable from './IssuesTable';
import { Box, Stack } from '@mui/material';
import { DataTableVariant } from '../common/table/TableTypes';
import { useCallback, useState } from 'react';
import IssuesFilters, { IssuesFiltersType } from './IssuesFilters';
import { SearchInput } from '../common/SearchInput';
import { filterItemsBySearch } from '../../lib/filterItemsBySearch';
import { Issue } from './issuesTypes';
import { OnUpdateIssueStatus } from '../aiAgentCenter/aiAgentsConsts';
import ToggleExpandButton from '../aiAgentCenter/ToggleExpandButton';
import { ActiveScan, ScanStatus } from '@server/issues';
import { SortOrder } from '../common/types';
import ScanningIndicator from './ScanningIndicator';

const getFiltersInitialState = () => ({
  selectedConfigurationTypes: [],
  selectedIssues: [],
  selectedSeverity: [],
  selectedStatus: ['open'],
});

const IssuesTableWithFilters = ({
  onUpdateIssueStatus,
  issues,
  activeScan,
  onTextSelection,
  isPreviewMode,
  tableInitialHeight,
  onExpandTableRow,
  expandedRowId,
  setExpandedRowId,
  tableRef,
}: {
  onUpdateIssueStatus: OnUpdateIssueStatus;
  issues: Issue[];
  activeScan?: ActiveScan;
  onTextSelection?: (text: string | null) => void;
  isPreviewMode: boolean;
  tableInitialHeight?: number;
  onExpandTableRow?: () => void;
  expandedRowId?: string;
  setExpandedRowId?: (id: string | undefined) => void;
  tableRef?: React.RefObject<any>;
}) => {
  const [filters, setFilters] = useState<IssuesFiltersType>(getFiltersInitialState());
  const [search, setSearch] = useState('');
  const [sortProps, setSortProps] = useState<{ sortBy: string; sortOrder: SortOrder }>({
    sortBy: 'resourceName',
    sortOrder: SortOrder.ASC,
  });

  const onSortChange = useCallback(
    ({ sortBy, sortOrder }: { sortBy: string; sortOrder: SortOrder }) => {
      setSortProps({ sortBy, sortOrder: sortOrder });
    },
    [],
  );

  const filteredIssues = issues.filter((issue: Issue) => {
    const bySeverity =
      filters.selectedSeverity.length > 0
        ? filters.selectedSeverity.includes(issue.severity)
        : true;
    const byType =
      filters.selectedConfigurationTypes.length > 0
        ? filters.selectedConfigurationTypes.includes(issue.resourceType)
        : true;
    const byIssue =
      filters.selectedIssues.length > 0
        ? filters.selectedIssues.includes(issue.bestPracticeName)
        : true;
    const byStatus =
      filters.selectedStatus.length > 0 ? filters.selectedStatus.includes(issue.status) : true;
    return byType && bySeverity && byIssue && byStatus;
  });

  const itemsToRender = filterItemsBySearch<Issue>(filteredIssues, search, 'resourceName');

  const setFilter = useCallback(
    (partialFilters: Partial<IssuesFiltersType>) => {
      setFilters((prev) => ({ ...prev, ...partialFilters }));
    },
    [setFilters],
  );

  const searchJsx = (
    <SearchInput
      TextFieldProps={{
        value: search,
        placeholder: 'Search',
        onChange: (e) => {
          setSearch(e.target.value);
        },
      }}
      withFixedMagnifyingGlassIcon={true}
      onClearButtonClick={() => setSearch('')}
      size="small"
    />
  );

  const isScanInProgress = activeScan && activeScan?.status !== ScanStatus.DONE;

  return (
    <Stack gap={2}>
      {isScanInProgress && <ScanningIndicator />}
      {!isPreviewMode && (
        <Stack gap={2}>
          <IssuesFilters
            issues={issues}
            filters={filters}
            setFilter={setFilter}
            searchJsx={searchJsx}
          />
          <IssuesTable
            tableVariant={DataTableVariant.medium}
            issues={itemsToRender}
            tableInitialHeight={tableInitialHeight ?? 430}
            onUpdateIssueStatus={onUpdateIssueStatus}
            onTextSelection={onTextSelection}
            isPreviewMode={false}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
            tableRef={tableRef}
            onSortChange={onSortChange}
            sortProps={sortProps}
          />
        </Stack>
      )}

      {isPreviewMode && (
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <IssuesTable
            tableVariant={DataTableVariant.small}
            issues={itemsToRender}
            tableInitialHeight={200}
            onUpdateIssueStatus={onUpdateIssueStatus}
            onTextSelection={onTextSelection}
            isPreviewMode={true}
            onExpandTableRow={onExpandTableRow}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
            tableRef={tableRef}
            onSortChange={onSortChange}
            sortProps={sortProps}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '4px',
              right: '6px',
              zIndex: 5,
            }}
          >
            <ToggleExpandButton />
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default IssuesTableWithFilters;
